import React, { useState, useRef, useEffect } from "react";
import { Tooltip } from "@fluentui/react-components";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import style from "./StatusDropdown.module.scss";

const STATUSES = [
    { label: "On Target",   colorClass: "HsOnTrack"  },
    { label: "Monitoring",  colorClass: "HsOffTrack" },
    { label: "Slipping",    colorClass: "HsAtRisk"   },
];

/**
 * HealthStatusDropdown
 *
 * Props:
 *  - tooltipText  {string}   — tooltip label; when falsy the Tooltip wrapper is omitted entirely
 *  - status       {number}   — controlled initial status index (0 = On Target, 1 = Monitoring, 2 = Slipping)
 *  - onChange     {function} — called with the new status index when the user selects a new value
 *  - disabled     {boolean}  — disables interaction
 *  - loading      {boolean}  — shows skeleton placeholder instead of the dropdown
 */
const HealthStatusDropdown = ({
    tooltipText,
    status = 0,
    onChange,
    disabled = false,
    loading = false,
}) => {
    const [selected, setSelected] = useState(status);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Sync controlled prop
    useEffect(() => {
        setSelected(status);
    }, [status]);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [isOpen]);

    const handleSelect = (e, index) => {
        e.preventDefault();
        setSelected(index);
        setIsOpen(false);
        if (onChange) onChange(index);
    };

    if (loading) {
        return (
            <div className={style.skeletonWrapper}>
                <Skeleton
                    width={85}
                    height={22}
                    borderRadius={99}
                    baseColor="#e0e0e0"
                    highlightColor="#ECE7FD"
                />
            </div>
        );
    }

    const currentStatus = STATUSES[selected] ?? STATUSES[0];

    const dropdown = (
        <div className={`${style.dropDowanContainer} ${disabled ? style.disabledContainer : ""}`}>
            <div className="position-relative" ref={containerRef}>
                <div
                    onClick={() => setIsOpen((prev) => !prev)}
                    className={`${style.dropdown_btn} ${style[currentStatus.colorClass]} text-xsm-600`}
                >
                    {currentStatus.label}
                    <span className={isOpen ? "fas fa-caret-up" : "fas fa-caret-down"} />
                </div>

                <div className={style.dropdown_content} style={{ display: isOpen ? "block" : "none" }}>
                    {STATUSES.map((s, i) => (
                        <div
                            key={i}
                            className={`${style.dropDownitem} text-xsm-400`}
                            onClick={(e) => handleSelect(e, i)}
                        >
                            <span className={`${style[s.colorClass]} ${style.radioBOx}`} />
                            <span className="text-xsm-400">{s.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (!tooltipText) return dropdown;

    return (
        <Tooltip content={tooltipText} relationship="label">
            {dropdown}
        </Tooltip>
    );
};

export default HealthStatusDropdown;
